import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function WhatsAppPanel({ token, t, showNotification }) {
  const [activeSubTab, setActiveSubTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({
    apiType: 'ultramsg',
    ultraMsgInstanceId: '',
    ultraMsgToken: '',
    metaAccessToken: '',
    metaPhoneNumberId: '',
    metaBusinessAccountId: '',
    wahaApiUrl: '',
    wahaSessionName: 'default',
    wahaApiKey: '',
    twilioAccountSid: '',
    twilioAuthToken: '',
    twilioWhatsAppNumber: '',
    autoSendFirstAd: true,
    minDelayBetweenMessages: 3,
    maxDelayBetweenMessages: 10,
    pauseAfterMessages: 100,
    pauseDuration: 180,
    isActive: true,
    adMessage1: '', adMessage1Attachment: '', adMessage1AttachmentType: '', adMessage1AttachmentName: '', adDay1: 0,
    adMessage2: '', adMessage2Attachment: '', adMessage2AttachmentType: '', adMessage2AttachmentName: '', adDay2: 1,
    adMessage3: '', adMessage3Attachment: '', adMessage3AttachmentType: '', adMessage3AttachmentName: '', adDay3: 2,
    adMessage4: '', adMessage4Attachment: '', adMessage4AttachmentType: '', adMessage4AttachmentName: '', adDay4: 3,
    adMessage5: '', adMessage5Attachment: '', adMessage5AttachmentType: '', adMessage5AttachmentName: '', adDay5: 5,
    adMessage6: '', adMessage6Attachment: '', adMessage6AttachmentType: '', adMessage6AttachmentName: '', adDay6: 7,
    adMessage7: '', adMessage7Attachment: '', adMessage7AttachmentType: '', adMessage7AttachmentName: '', adDay7: 10,
    adMessage8: '', adMessage8Attachment: '', adMessage8AttachmentType: '', adMessage8AttachmentName: '', adDay8: 14,
  });
  
  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactSearch, setContactSearch] = useState('');
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [newContact, setNewContact] = useState({ phone: '', name: '', email: '' });
  const [csvFile, setCsvFile] = useState(null);
  
  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    response: '',
    attachmentUrl: '',
    attachmentType: '',
    attachmentName: '',
    matchType: 'contains',
    priority: 0
  });
  
  // Bulk Jobs
  const [bulkJobs, setBulkJobs] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    name: '',
    message: '',
    attachmentUrl: '',
    attachmentType: '',
    attachmentName: '',
    phones: '',
    useAllContacts: true,
    minDelay: 3,
    maxDelay: 10,
    pauseAfter: 100,
    pauseDuration: 180
  });
  
  // Messages
  const [messages, setMessages] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotalPages, setMessagesTotalPages] = useState(1);
  
  // Stats
  const [stats, setStats] = useState({
    totalContacts: 0,
    activeContacts: 0,
    contactsWithReplies: 0,
    totalMessagesSent: 0,
    totalMessagesReceived: 0,
    totalAdsSent: 0,
    totalKeywordReplies: 0,
    activeKeywords: 0,
    runningJobs: 0
  });
  
  // Test connection
  const [testPhone, setTestPhone] = useState('');
  const [syncing, setSyncing] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Load Settings
  const loadSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/settings`, { headers });
      const data = await response.json();
      setSettings(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error loading WhatsApp settings:', error);
    }
  }, [token]);

  // Load Contacts
  const loadContacts = useCallback(async (page = 1, search = '') => {
    try {
      const url = new URL(`${API_URL}/api/whatsapp/contacts`);
      url.searchParams.append('page', page);
      url.searchParams.append('limit', 20);
      if (search) url.searchParams.append('search', search);
      
      const response = await fetch(url, { headers });
      const data = await response.json();
      
      setContacts(data.contacts || []);
      setContactsTotalPages(data.pages || 1);
      setContactsTotal(data.total || 0);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  }, [token]);

  // Load Keywords
  const loadKeywords = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/keywords`, { headers });
      const data = await response.json();
      setKeywords(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading WhatsApp keywords:', error);
      setKeywords([]);
    }
  }, [token]);

  // Load Bulk Jobs
  const loadBulkJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/bulk-jobs`, { headers });
      const data = await response.json();
      setBulkJobs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading bulk jobs:', error);
      setBulkJobs([]);
    }
  }, [token]);

  // Load Messages
  const loadMessages = useCallback(async (page = 1) => {
    try {
      const url = new URL(`${API_URL}/api/whatsapp/messages`);
      url.searchParams.append('page', page);
      url.searchParams.append('limit', 50);
      
      const response = await fetch(url, { headers });
      const data = await response.json();
      
      setMessages(data.messages || []);
      setMessagesTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [token]);

  // Load Stats
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/stats`, { headers });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading WhatsApp stats:', error);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
    loadContacts();
    loadKeywords();
    loadBulkJobs();
    loadMessages();
    loadStats();
  }, [loadSettings, loadContacts, loadKeywords, loadBulkJobs, loadMessages, loadStats]);

  // Refresh bulk jobs periodically
  useEffect(() => {
    const interval = setInterval(() => {
      loadBulkJobs();
      loadStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadBulkJobs, loadStats]);

  // Sync all existing leads with WhatsApp
  const syncLeadsToWhatsApp = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/sync-whatsapp`, {
        method: 'POST',
        headers
      });
      const data = await response.json();
      if (data.added !== undefined) {
        showNotification(`${data.message}`, 'success');
        loadContacts();
        loadStats();
      } else {
        showNotification(data.message || t.messages.error, 'error');
      }
    } catch (error) {
      console.error('Error syncing leads:', error);
      showNotification(t.messages.error, 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Save Settings
  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (data.success) {
        showNotification(t.messages.saved);
      } else {
        showNotification(data.error || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Test Connection
  const testConnection = async () => {
    if (!testPhone) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/settings/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ phone: testPhone })
      });
      const data = await response.json();
      if (data.success) {
        showNotification(t.messages.sent);
      } else {
        showNotification(data.error || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Add Contact
  const addContact = async () => {
    if (!newContact.phone) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/contacts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newContact)
      });
      const data = await response.json();
      if (data.success) {
        setShowAddContactModal(false);
        setNewContact({ phone: '', name: '', email: '' });
        loadContacts(contactsPage, contactSearch);
        loadStats();
        showNotification(t.messages.success);
      } else {
        showNotification(data.error || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Import CSV
  const importCSV = async () => {
    if (!csvFile) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('skipFirstRow', 'true');
      
      const response = await fetch(`${API_URL}/api/whatsapp/contacts/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setShowImportModal(false);
        setCsvFile(null);
        loadContacts();
        loadStats();
        showNotification(`${t.messages.success}: ${data.added} added, ${data.skipped} skipped`);
      } else {
        showNotification(data.error || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete Contact
  const deleteContact = async (id) => {
    if (!window.confirm(t.messages.confirmDelete)) return;
    try {
      await fetch(`${API_URL}/api/whatsapp/contacts/${id}`, {
        method: 'DELETE',
        headers
      });
      loadContacts(contactsPage, contactSearch);
      loadStats();
      showNotification(t.messages.deleted);
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Add Keyword
  const addKeyword = async () => {
    if (!newKeyword.keyword || !newKeyword.response) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/keywords`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newKeyword)
      });
      const data = await response.json();
      if (data.success) {
        setNewKeyword({
          keyword: '',
          response: '',
          attachmentUrl: '',
          attachmentType: '',
          attachmentName: '',
          matchType: 'contains',
          priority: 0
        });
        loadKeywords();
        showNotification(t.messages.keywordAdded);
      } else {
        showNotification(data.error || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Delete Keyword
  const deleteKeyword = async (id) => {
    if (!window.confirm(t.messages.confirmDelete)) return;
    try {
      await fetch(`${API_URL}/api/whatsapp/keywords/${id}`, {
        method: 'DELETE',
        headers
      });
      loadKeywords();
      showNotification(t.messages.keywordDeleted);
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Toggle Keyword
  const toggleKeyword = async (id) => {
    try {
      const keyword = keywords.find(k => k._id === id);
      await fetch(`${API_URL}/api/whatsapp/keywords/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isActive: !keyword.isActive })
      });
      loadKeywords();
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Upload Attachment (for keywords or bulk)
  const uploadAttachment = async (file, type = 'keyword') => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/keywords/upload-attachment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  // Start Bulk Job
  const startBulkJob = async () => {
    if (!bulkForm.message) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }
    
    setLoading(true);
    try {
      let phones = [];
      if (bulkForm.useAllContacts) {
        const response = await fetch(`${API_URL}/api/whatsapp/contacts/phones`, { headers });
        const data = await response.json();
        phones = data.map(c => c.phone);
      } else {
        phones = bulkForm.phones.split(',').map(p => p.trim()).filter(p => p);
      }
      
      if (phones.length === 0) {
        showNotification('No contacts selected', 'error');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/whatsapp/bulk-jobs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: bulkForm.name || 'Bulk Message',
          message: bulkForm.message,
          phones: phones,
          attachmentUrl: bulkForm.attachmentUrl,
          attachmentType: bulkForm.attachmentType,
          attachmentName: bulkForm.attachmentName,
          minDelay: bulkForm.minDelay,
          maxDelay: bulkForm.maxDelay,
          pauseAfter: bulkForm.pauseAfter,
          pauseDuration: bulkForm.pauseDuration
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setShowBulkModal(false);
        setBulkForm({
          name: '',
          message: '',
          attachmentUrl: '',
          attachmentType: '',
          attachmentName: '',
          phones: '',
          useAllContacts: true,
          minDelay: 3,
          maxDelay: 10,
          pauseAfter: 100,
          pauseDuration: 180
        });
        loadBulkJobs();
        showNotification(t.messages.success);
      } else {
        showNotification(data.error || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Pause/Resume/Cancel Bulk Job
  const pauseBulkJob = async (id) => {
    try {
      await fetch(`${API_URL}/api/whatsapp/bulk-jobs/${id}/pause`, {
        method: 'POST',
        headers
      });
      loadBulkJobs();
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  const resumeBulkJob = async (id) => {
    try {
      await fetch(`${API_URL}/api/whatsapp/bulk-jobs/${id}/resume`, {
        method: 'POST',
        headers
      });
      loadBulkJobs();
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  const cancelBulkJob = async (id) => {
    if (!window.confirm(t.messages.confirmDelete)) return;
    try {
      await fetch(`${API_URL}/api/whatsapp/bulk-jobs/${id}/cancel`, {
        method: 'POST',
        headers
      });
      loadBulkJobs();
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Sub-tabs
  const subTabs = [
    { id: 'settings', label: t.whatsapp.settings },
    { id: 'ads', label: t.whatsapp.adMessages },
    { id: 'contacts', label: t.whatsapp.contacts },
    { id: 'keywords', label: t.whatsapp.keywords },
    { id: 'bulk', label: t.whatsapp.bulkMessages },
    { id: 'messages', label: t.whatsapp.messages }
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-700/30 rounded-xl p-4">
          <div className="text-2xl font-bold text-emerald-400">{stats.totalContacts}</div>
          <div className="text-sm text-slate-400">{t.whatsapp.totalContacts}</div>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-4">
          <div className="text-2xl font-bold text-blue-400">{stats.totalMessagesSent}</div>
          <div className="text-sm text-slate-400">{t.whatsapp.totalMessagesSent}</div>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-4">
          <div className="text-2xl font-bold text-purple-400">{stats.contactsWithReplies}</div>
          <div className="text-sm text-slate-400">{t.whatsapp.contactsWithReplies}</div>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-4">
          <div className="text-2xl font-bold text-amber-400">{stats.runningJobs}</div>
          <div className="text-sm text-slate-400">{t.whatsapp.runningJobs}</div>
        </div>
      </div>

      {/* Sub-Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-700 pb-4">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSubTab === tab.id
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeSubTab === 'settings' && (
        <div className="space-y-6">
          {/* API Type Selection */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.apiType}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['ultramsg', 'meta-cloud-api', 'waha', 'twilio'].map(api => (
                <button
                  key={api}
                  onClick={() => setSettings(prev => ({ ...prev, apiType: api }))}
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    settings.apiType === api
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <div className="text-white font-medium">{api.toUpperCase()}</div>
                </button>
              ))}
            </div>
          </div>

          {/* API Settings based on type */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            {settings.apiType === 'ultramsg' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.ultraMsgSettings}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.instanceId}</label>
                    <input
                      type="text"
                      value={settings.ultraMsgInstanceId}
                      onChange={(e) => setSettings(prev => ({ ...prev, ultraMsgInstanceId: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.token}</label>
                    <input
                      type="password"
                      value={settings.ultraMsgToken}
                      onChange={(e) => setSettings(prev => ({ ...prev, ultraMsgToken: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {settings.apiType === 'meta-cloud-api' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.metaSettings}</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.accessToken}</label>
                    <input
                      type="password"
                      value={settings.metaAccessToken}
                      onChange={(e) => setSettings(prev => ({ ...prev, metaAccessToken: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.phoneNumberId}</label>
                      <input
                        type="text"
                        value={settings.metaPhoneNumberId}
                        onChange={(e) => setSettings(prev => ({ ...prev, metaPhoneNumberId: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.businessAccountId}</label>
                      <input
                        type="text"
                        value={settings.metaBusinessAccountId}
                        onChange={(e) => setSettings(prev => ({ ...prev, metaBusinessAccountId: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {settings.apiType === 'waha' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.wahaSettings}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.apiUrl}</label>
                    <input
                      type="text"
                      value={settings.wahaApiUrl}
                      onChange={(e) => setSettings(prev => ({ ...prev, wahaApiUrl: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="http://localhost:3000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.sessionName}</label>
                    <input
                      type="text"
                      value={settings.wahaSessionName}
                      onChange={(e) => setSettings(prev => ({ ...prev, wahaSessionName: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.apiKey}</label>
                    <input
                      type="password"
                      value={settings.wahaApiKey}
                      onChange={(e) => setSettings(prev => ({ ...prev, wahaApiKey: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {settings.apiType === 'twilio' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.twilioSettings}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.accountSid}</label>
                    <input
                      type="text"
                      value={settings.twilioAccountSid}
                      onChange={(e) => setSettings(prev => ({ ...prev, twilioAccountSid: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.authToken}</label>
                    <input
                      type="password"
                      value={settings.twilioAuthToken}
                      onChange={(e) => setSettings(prev => ({ ...prev, twilioAuthToken: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.whatsappNumber}</label>
                    <input
                      type="text"
                      value={settings.twilioWhatsAppNumber}
                      onChange={(e) => setSettings(prev => ({ ...prev, twilioWhatsAppNumber: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Delay Settings */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.delaySettings}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.minDelay}</label>
                <input
                  type="number"
                  value={settings.minDelayBetweenMessages}
                  onChange={(e) => setSettings(prev => ({ ...prev, minDelayBetweenMessages: parseInt(e.target.value) }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.maxDelay}</label>
                <input
                  type="number"
                  value={settings.maxDelayBetweenMessages}
                  onChange={(e) => setSettings(prev => ({ ...prev, maxDelayBetweenMessages: parseInt(e.target.value) }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.pauseAfter}</label>
                <input
                  type="number"
                  value={settings.pauseAfterMessages}
                  onChange={(e) => setSettings(prev => ({ ...prev, pauseAfterMessages: parseInt(e.target.value) }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.pauseDuration}</label>
                <input
                  type="number"
                  value={settings.pauseDuration}
                  onChange={(e) => setSettings(prev => ({ ...prev, pauseDuration: parseInt(e.target.value) }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Auto Send First Ad */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.autoSendFirstAd}
                onChange={(e) => setSettings(prev => ({ ...prev, autoSendFirstAd: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-white">{t.whatsapp.autoSendFirstAd}</span>
            </label>
          </div>

          {/* System Active */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.isActive}
                onChange={(e) => setSettings(prev => ({ ...prev, isActive: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-white">{t.settings.enable} WhatsApp</span>
            </label>
          </div>

          {/* Test Connection */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t.whatsapp.testConnection}</h3>
            <div className="flex gap-4">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                className="flex-1 px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder={t.whatsapp.testPhone}
              />
              <button
                onClick={testConnection}
                disabled={loading}
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {t.whatsapp.testConnection}
              </button>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={saveSettings}
            disabled={loading}
            className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? t.common.loading : t.settings.saveSettings}
          </button>
        </div>
      )}

      {/* Ad Messages Tab */}
      {activeSubTab === 'ads' && (
        <div className="space-y-4">
          {/* Variables hint */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-400">{t.adTemplates.variablesHint || 'Variables:'}</span>
            <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-emerald-300">{'{name}'}</code>
            <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-emerald-300">{'{phone}'}</code>
            <code className="px-2 py-0.5 bg-slate-700 rounded text-xs text-emerald-300">{'{email}'}</code>
          </div>
          
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(num => {
              const isActive = settings[`adActive${num}`] !== false;
              const sendDay = settings[`adDay${num}`] || 0;
              
              const getSendDayLabel = (day) => {
                const isRTL = document.documentElement.dir === 'rtl';
                if (day === 0) return isRTL ? 'فوري' : 'Immediately';
                if (day === 1) return isRTL ? 'بعد يوم' : 'After 1 day';
                if (day === 2) return isRTL ? 'بعد يومين' : 'After 2 days';
                if (day === 7) return isRTL ? 'بعد اسبوع' : 'After 1 week';
                if (day === 14) return isRTL ? 'بعد اسبوعين' : 'After 2 weeks';
                return isRTL ? `بعد ${day} ايام` : `After ${day} days`;
              };
              
              return (
                <div 
                  key={num} 
                  className={`bg-slate-800/50 rounded-lg p-3 border transition-all ${
                    isActive ? 'border-emerald-500/30' : 'border-slate-700 opacity-60'
                  }`}
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 text-emerald-400 rounded-full font-bold text-xs">
                        {num}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        sendDay === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-600 text-slate-300'
                      }`}>
                        {getSendDayLabel(sendDay)}
                      </span>
                    </div>
                    
                    {/* Toggle Switch */}
                    <div className="flex items-center gap-2">
                      <select
                        value={sendDay}
                        onChange={(e) => setSettings(prev => ({ ...prev, [`adDay${num}`]: parseInt(e.target.value) || 0 }))}
                        className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                      >
                        <option value={0}>{document.documentElement.dir === 'rtl' ? 'فوري' : 'Now'}</option>
                        <option value={1}>{document.documentElement.dir === 'rtl' ? 'بعد يوم' : '1 day'}</option>
                        <option value={2}>{document.documentElement.dir === 'rtl' ? 'بعد يومين' : '2 days'}</option>
                        <option value={3}>{document.documentElement.dir === 'rtl' ? '3 ايام' : '3 days'}</option>
                        <option value={5}>{document.documentElement.dir === 'rtl' ? '5 ايام' : '5 days'}</option>
                        <option value={7}>{document.documentElement.dir === 'rtl' ? 'اسبوع' : '1 week'}</option>
                        <option value={14}>{document.documentElement.dir === 'rtl' ? 'اسبوعين' : '2 weeks'}</option>
                      </select>
                      
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, [`adActive${num}`]: !isActive }))}
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          isActive ? 'bg-emerald-500' : 'bg-slate-600'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          isActive ? 'translate-x-5' : 'translate-x-0.5'
                        }`}></div>
                      </button>
                    </div>
                  </div>
                  
                  <textarea
                    value={settings[`adMessage${num}`] || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, [`adMessage${num}`]: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    placeholder={`${t.whatsapp.message} ${num}...`}
                    disabled={!isActive}
                  />
                </div>
              );
            })}
          </div>
          
          <button
            onClick={saveSettings}
            disabled={loading}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? t.common.loading : t.settings.saveSettings}
          </button>
        </div>
      )}

      {/* Contacts Tab */}
      {activeSubTab === 'contacts' && (
        <div className="space-y-4">
          {/* Actions */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <form onSubmit={(e) => { e.preventDefault(); loadContacts(1, contactSearch); }} className="flex gap-2">
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder={t.common.search}
              />
              <button type="submit" className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-xl">
                {t.common.search}
              </button>
            </form>
            
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowAddContactModal(true)}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium"
              >
                {t.whatsapp.addContact}
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium"
              >
                {t.whatsapp.importCSV}
              </button>
              <button
                onClick={syncLeadsToWhatsApp}
                disabled={syncing}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                {syncing ? t.common.loading : (t.whatsapp.syncFromEmail || 'Sync from Email Leads')}
              </button>
            </div>
          </div>

          {/* Contacts Table */}
          <div className="bg-slate-700/30 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.phone}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.name}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.source}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.adsSent}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.status}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.actions}</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact._id} className="border-t border-slate-700">
                    <td className="px-4 py-3 text-white">{contact.phone}</td>
                    <td className="px-4 py-3 text-slate-300">{contact.name || '-'}</td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{contact.source}</td>
                    <td className="px-4 py-3 text-slate-300">{contact.adMessagesSent || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        contact.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                        contact.status === 'blocked' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {contact.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteContact(contact._id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        {t.common.delete}
                      </button>
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t.common.noData}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {contactsTotalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => { setContactsPage(p => Math.max(1, p - 1)); loadContacts(contactsPage - 1, contactSearch); }}
                disabled={contactsPage <= 1}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50"
              >
                {t.common.previous}
              </button>
              <span className="px-4 py-2 text-slate-300">{contactsPage} / {contactsTotalPages}</span>
              <button
                onClick={() => { setContactsPage(p => Math.min(contactsTotalPages, p + 1)); loadContacts(contactsPage + 1, contactSearch); }}
                disabled={contactsPage >= contactsTotalPages}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50"
              >
                {t.common.next}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Keywords Tab */}
      {activeSubTab === 'keywords' && (
        <div className="space-y-6">
          {/* Webhook Setup Info */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <h4 className="text-amber-400 font-semibold mb-2">{t.whatsapp.webhookSetup || 'Webhook Setup Required'}</h4>
            <p className="text-sm text-slate-300 mb-2">
              {t.whatsapp.webhookInfo || 'To receive incoming WhatsApp messages and auto-reply with keywords, configure this webhook URL in your WhatsApp API provider:'}
            </p>
            <div className="bg-slate-800 rounded-lg p-3 mb-3">
              <code className="text-emerald-400 text-sm break-all">
                {window.location.origin.replace(':3000', ':5000')}/api/whatsapp/webhook
              </code>
            </div>
            <p className="text-xs text-slate-400">
              {t.whatsapp.webhookInstructions || 'Copy this URL and paste it in your UltraMsg/Meta/WAHA webhook settings.'}
            </p>
          </div>

          {/* Test Keyword Feature */}
          <div className="bg-slate-700/30 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">{t.whatsapp.testKeyword || 'Test Keyword Matching'}</h4>
            <div className="flex gap-4 flex-wrap">
              <input
                type="text"
                placeholder={t.whatsapp.testPhone || 'Phone number (e.g. +96171910201)'}
                id="test-keyword-phone"
                className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 w-48"
              />
              <input
                type="text"
                placeholder={t.whatsapp.testMessage || 'Test message with keyword'}
                id="test-keyword-message"
                className="flex-1 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                onClick={async () => {
                  const phone = document.getElementById('test-keyword-phone').value;
                  const message = document.getElementById('test-keyword-message').value;
                  if (!phone || !message) {
                    showNotification('Enter phone and message', 'error');
                    return;
                  }
                  try {
                    // Use webhook test endpoint which simulates real incoming message
                    const res = await fetch(`${API_URL}/api/whatsapp/webhook/test`, {
                      method: 'POST',
                      headers: { ...headers, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ phone, message })
                    });
                    const data = await res.json();
                    console.log('Test result:', data);
                    if (data.keywordMatched) {
                      showNotification(`Keyword "${data.keyword}" matched - Reply sent to ${phone}!`, 'success');
                      loadKeywords();
                    } else {
                      showNotification('No keyword matched in message', 'error');
                    }
                  } catch (err) {
                    showNotification('Test failed: ' + err.message, 'error');
                  }
                }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium"
              >
                {t.whatsapp.testButton || 'Test'}
              </button>
            </div>
          </div>

          {/* Add Keyword Form */}
          <div className="bg-slate-700/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">{t.whatsapp.keywordTitle}</h3>
            <p className="text-sm text-slate-400 mb-4">{t.whatsapp.keywordSubtitle}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.keyword}</label>
                <input
                  type="text"
                  value={newKeyword.keyword}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, keyword: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.matchType}</label>
                <select
                  value={newKeyword.matchType}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, matchType: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="contains">{t.whatsapp.contains}</option>
                  <option value="exact">{t.whatsapp.exact}</option>
                  <option value="startsWith">{t.whatsapp.startsWith}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.priority}</label>
                <input
                  type="number"
                  value={newKeyword.priority}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.response}</label>
              <textarea
                value={newKeyword.response}
                onChange={(e) => setNewKeyword(prev => ({ ...prev, response: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>
            
            <button
              onClick={addKeyword}
              className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium"
            >
              {t.common.add}
            </button>
          </div>

          {/* Keywords List */}
          <div className="bg-slate-700/30 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.keyword}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.response}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.timesTriggered}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.status}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.actions}</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map(kw => (
                  <tr key={kw._id} className="border-t border-slate-700">
                    <td className="px-4 py-3 text-white font-medium">{kw.keyword}</td>
                    <td className="px-4 py-3 text-slate-300 text-sm max-w-xs truncate">{kw.response}</td>
                    <td className="px-4 py-3 text-slate-300">{kw.timesTriggered || 0}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleKeyword(kw._id)}
                        className={`px-2 py-1 rounded-full text-xs ${
                          kw.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                        }`}
                      >
                        {kw.isActive ? t.whatsapp.active : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteKeyword(kw._id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        {t.common.delete}
                      </button>
                    </td>
                  </tr>
                ))}
                {keywords.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t.keywords.noKeywords}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Messages Tab */}
      {activeSubTab === 'bulk' && (
        <div className="space-y-6">
          {/* Variables hint for bulk messages */}
          <div className="bg-slate-800/50 border border-emerald-500/30 rounded-xl p-4">
            <p className="text-sm text-emerald-400 font-medium mb-2">{t.adTemplates.variablesHint || 'Available Variables:'}</p>
            <div className="flex flex-wrap gap-2">
              <code className="px-2 py-1 bg-slate-700 rounded text-xs text-emerald-300">{'{name}'}</code>
              <span className="text-slate-400 text-xs">-</span>
              <span className="text-slate-300 text-xs">{t.adTemplates.variableName || "Client's name"}</span>
              <span className="text-slate-600 text-xs mx-2">|</span>
              <code className="px-2 py-1 bg-slate-700 rounded text-xs text-emerald-300">{'{phone}'}</code>
              <span className="text-slate-400 text-xs">-</span>
              <span className="text-slate-300 text-xs">{t.adTemplates.variablePhone || "Client's phone"}</span>
              <span className="text-slate-600 text-xs mx-2">|</span>
              <code className="px-2 py-1 bg-slate-700 rounded text-xs text-emerald-300">{'{email}'}</code>
              <span className="text-slate-400 text-xs">-</span>
              <span className="text-slate-300 text-xs">{t.adTemplates.variableEmail || "Client's email"}</span>
            </div>
            <p className="text-xs text-slate-500 mt-2">{t.whatsapp.bulkVariablesNote || 'Use these variables to personalize each message with client info'}</p>
          </div>

          <button
            onClick={() => setShowBulkModal(true)}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium"
          >
            {t.whatsapp.createBulkJob}
          </button>

          {/* Bulk Jobs List */}
          <div className="bg-slate-700/30 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.jobName}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.progress}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.sent}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.failed}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.status}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.actions}</th>
                </tr>
              </thead>
              <tbody>
                {bulkJobs.map(job => (
                  <tr key={job._id} className="border-t border-slate-700">
                    <td className="px-4 py-3 text-white">{job.name}</td>
                    <td className="px-4 py-3">
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-emerald-500 h-2 rounded-full transition-all"
                          style={{ width: `${(job.currentIndex / job.totalContacts) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400">{job.currentIndex} / {job.totalContacts}</span>
                    </td>
                    <td className="px-4 py-3 text-emerald-400">{job.sentCount}</td>
                    <td className="px-4 py-3 text-red-400">{job.failedCount}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        job.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                        job.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        job.status === 'paused' ? 'bg-amber-500/20 text-amber-400' :
                        job.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 flex gap-2">
                      {job.status === 'running' && (
                        <button
                          onClick={() => pauseBulkJob(job._id)}
                          className="text-amber-400 hover:text-amber-300 text-sm"
                        >
                          {t.whatsapp.pauseJob}
                        </button>
                      )}
                      {job.status === 'paused' && (
                        <button
                          onClick={() => resumeBulkJob(job._id)}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          {t.whatsapp.resumeJob}
                        </button>
                      )}
                      {['running', 'paused', 'pending'].includes(job.status) && (
                        <button
                          onClick={() => cancelBulkJob(job._id)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          {t.whatsapp.cancelJob}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {bulkJobs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t.common.noData}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Messages Tab */}
      {activeSubTab === 'messages' && (
        <div className="space-y-4">
          <div className="bg-slate-700/30 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.phone}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.message}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">Type</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.whatsapp.status}</th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.replies.date}</th>
                </tr>
              </thead>
              <tbody>
                {messages.map(msg => (
                  <tr key={msg._id} className="border-t border-slate-700">
                    <td className="px-4 py-3 text-white">{msg.phone}</td>
                    <td className="px-4 py-3 text-slate-300 text-sm max-w-xs truncate">{msg.message}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        msg.direction === 'incoming' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {msg.direction === 'incoming' ? t.whatsapp.incoming : t.whatsapp.outgoing}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        msg.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' :
                        msg.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {msg.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">
                      {new Date(msg.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {messages.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-400">{t.common.noData}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {messagesTotalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => { setMessagesPage(p => Math.max(1, p - 1)); loadMessages(messagesPage - 1); }}
                disabled={messagesPage <= 1}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50"
              >
                {t.common.previous}
              </button>
              <span className="px-4 py-2 text-slate-300">{messagesPage} / {messagesTotalPages}</span>
              <button
                onClick={() => { setMessagesPage(p => Math.min(messagesTotalPages, p + 1)); loadMessages(messagesPage + 1); }}
                disabled={messagesPage >= messagesTotalPages}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50"
              >
                {t.common.next}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{t.whatsapp.addContact}</h3>
              <button onClick={() => setShowAddContactModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.phone} *</label>
                <input
                  type="text"
                  value={newContact.phone}
                  onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="+49123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.name}</label>
                <input
                  type="text"
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.email}</label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddContactModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={addContact}
                disabled={loading}
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                {loading ? t.common.loading : t.common.add}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{t.whatsapp.importCSV}</h3>
              <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-400">CSV format: phone,name,email (first row is header)</p>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white"
              />
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowImportModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={importCSV}
                disabled={loading || !csvFile}
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                {loading ? t.common.loading : t.whatsapp.importCSV}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Message Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{t.whatsapp.createBulkJob}</h3>
              <button onClick={() => setShowBulkModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.jobName}</label>
                <input
                  type="text"
                  value={bulkForm.name}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.message} *</label>
                <textarea
                  value={bulkForm.message}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  id="bulk-file"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const result = await uploadAttachment(file);
                      if (result) {
                        setBulkForm(prev => ({
                          ...prev,
                          attachmentUrl: result.url,
                          attachmentType: result.type,
                          attachmentName: result.name
                        }));
                      }
                    }
                  }}
                  className="hidden"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                />
                <label
                  htmlFor="bulk-file"
                  className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg cursor-pointer text-sm"
                >
                  {t.whatsapp.uploadFile}
                </label>
                {bulkForm.attachmentUrl && (
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400 text-sm">{bulkForm.attachmentName}</span>
                    <button
                      onClick={() => setBulkForm(prev => ({ ...prev, attachmentUrl: '', attachmentType: '', attachmentName: '' }))}
                      className="text-red-400 hover:text-red-300"
                    >
                      X
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={bulkForm.useAllContacts}
                    onChange={() => setBulkForm(prev => ({ ...prev, useAllContacts: true }))}
                    className="w-4 h-4 text-emerald-500"
                  />
                  <span className="text-white">{t.whatsapp.allContacts} ({stats.activeContacts})</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={!bulkForm.useAllContacts}
                    onChange={() => setBulkForm(prev => ({ ...prev, useAllContacts: false }))}
                    className="w-4 h-4 text-emerald-500"
                  />
                  <span className="text-white">{t.whatsapp.pasteNumbers}</span>
                </label>
              </div>
              
              {!bulkForm.useAllContacts && (
                <div>
                  <textarea
                    value={bulkForm.phones}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, phones: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    placeholder={t.whatsapp.pasteNumbersPlaceholder}
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.minDelay}</label>
                  <input
                    type="number"
                    value={bulkForm.minDelay}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, minDelay: parseInt(e.target.value) || 3 }))}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.maxDelay}</label>
                  <input
                    type="number"
                    value={bulkForm.maxDelay}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, maxDelay: parseInt(e.target.value) || 10 }))}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.pauseAfter}</label>
                  <input
                    type="number"
                    value={bulkForm.pauseAfter}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, pauseAfter: parseInt(e.target.value) || 100 }))}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.whatsapp.pauseDuration}</label>
                  <input
                    type="number"
                    value={bulkForm.pauseDuration}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, pauseDuration: parseInt(e.target.value) || 180 }))}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowBulkModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={startBulkJob}
                disabled={loading}
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                {loading ? t.common.loading : t.whatsapp.startJob}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppPanel;
